<template>
    <div class="forms__field_input__files__preview-item" v-bind="$attrs">
        <div class="forms__field_input__files__preview-item__thumb" :class="[sizeClass]">
            <icon
                v-if="props.file.icon"
                :icon="props.file.icon"
                class="forms__field_input__files__preview-item__icon"
                :class="[`text-${props.file.icon_color}-400`]"
            ></icon>
            <icon v-else icon="hero/outline/paper-clip" class="forms__field_input__files__preview-item__icon"></icon>

            <div
                v-if="
                    typeof props.file.is_image !== 'undefined' &&
                    props.file.is_image !== '0' &&
                    props.file.id &&
                    props.file.is_image !== false
                "
                class="forms__field_input__files__preview-item__preview"
                :class="[
                    props.file.id
                        ? 'forms__field_input__files__preview-item__preview-saved'
                        : 'forms__field_input__files__preview-item__preview-new',
                ]"
                @click="props.file.id ? open($event, props.file) : ''"
            >
                <img
                    :src="route('media.admin.media.preview', { id: props.file.id })"
                    class="w-full h-full object-cover object-center"
                    loading="lazy"
                />
            </div>
            <div
                v-else-if="
                    typeof props.file.is_image !== 'undefined' &&
                    props.file.is_image !== '0' &&
                    props.file.is_image !== false
                "
                class="forms__field_input__files__preview-item__preview"
                :class="[
                    props.file.id
                        ? 'forms__field_input__files__preview-item__preview-saved'
                        : 'forms__field_input__files__preview-item__preview-new',
                ]"
                @click="props.file.id ? open($event, props.file) : ''"
            >
                <img :src="props.file.url" class="w-full h-full object-cover object-center" loading="lazy" />
            </div>
        </div>
        <div class="forms__field_input__files__preview-item__name">
            <h4 class="forms__field_input__files__preview-item__name-label">
                <span
                    target="_blank"
                    rel="noopener noreferrer"
                    :class="[
                        props.file.id
                            ? 'forms__field_input__files__preview-item__name-saved'
                            : 'forms__field_input__files__preview-item__name-new',
                        !props.file.valid && typeof props.file.valid !== 'undefined'
                            ? 'forms__field_input__files__preview-item__name-invalid'
                            : '',
                    ]"
                    @click="props.file.id ? open($event, props.file) : ''"
                >
                    {{ props.file.name }}
                </span>
            </h4>
            <span v-if="props.params?.display?.created_at">
                {{ lang('general.datagrid.created_at') }}:
                {{ props.file.created_at }}
            </span>
            <div v-if="uploadPercent !== undefined" class="flex items-center gap-2 mt-1">
                <div class="h-1 grow rounded bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
                    <div
                        class="h-1 rounded bg-blue-500 transition-[width] duration-150"
                        :style="{ width: `${uploadPercent}%` }"
                    ></div>
                </div>
                <span class="text-xs tabular-nums text-neutral-500">{{ uploadPercent }}&nbsp;%</span>
            </div>
        </div>
        <div v-tooltip="createdAtTooltip" class="forms__field_input__files__preview-item__size">
            {{ filesizeHumanReadable(props.file.size) }}<template v-if="extension"> · {{ extension }}</template>
        </div>
        <div class="forms__field_input__files__preview-item__actions">
            <div v-show="props.interactive" class="forms__field_input__files__preview-item__button" @click="remove">
                <icon class="forms__field_input__files__preview-item__button-icon" icon="hero/outline/trash"></icon>
            </div>

            <ul v-if="hasActions" class="forms__field_input__files__preview-item__actions-list">
                <li
                    v-for="action in props.actions"
                    :key="action.name"
                    class="forms__field_input__files__preview-item__action"
                >
                    <button
                        type="button"
                        class="forms__field_input__files__preview-item__action-button"
                        :class="[
                            action.callback
                                ? 'forms__field_input__files__preview-item__action-button--interactive'
                                : 'cursor-auto',
                        ]"
                        @click="action.callback ? action.callback(props.file) : ''"
                    >
                        {{ typeof action.name === 'function' ? action.name(props.file) : action.name }}
                    </button>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { PreviewFile } from '@src/Fields/Pages/Files/types';
import type { FieldAction } from '@src/Common/Components/types';
import { filesizeHumanReadable } from '@src/Common/Helpers/files';
import { DateHelper } from '@src/Common/Helpers/dates';
import { ComponentSize } from '@src/Common/DataGrid/types';
import { lang } from '@/lang/composable';

const props = withDefaults(
    defineProps<{
        file?: PreviewFile;
        interactive?: boolean;
        size?: ComponentSize;
        actions?: FieldAction[];
        params?: any;
        uploadProgress?: Map<File, number>;
    }>(),
    {
        file: {
            is_image: false,
            name: '',
            url: '',
            icon: '',
            icon_color: 'gray',
            size: 0,
            mime: '',
            created_at: '',
            alt: '',
            valid: true,
        },
        interactive: true,
        size: 'base',
        actions: () => [],
        uploadProgress: undefined,
        params: () => ({
            display: {
                created_at: false,
            },
        }),
    },
);

const emit = defineEmits<{
    (event: 'remove', content: PreviewFile): void;
    (event: 'open', content: PreviewFile): void;
}>();

/**
 * Full upload date+time shown on hover over the size/type part. Enabled via
 * params.display.created_at_tooltip — `true` for a generic label, or a string
 * with a custom label. Empty string disables the tooltip (directive no-op).
 */
const createdAtTooltip = computed((): string => {
    const flag = props.params?.display?.created_at_tooltip;

    if (!flag || !props.file.created_at) {
        return '';
    }

    const label = typeof flag === 'string' ? flag : lang('general.datagrid.created_at');

    try {
        const helper = new DateHelper(String(props.file.created_at));

        return `${label}: ${helper.toHumanDate()} ${helper.toHumanTime()}`;
    } catch {
        return `${label}: ${props.file.created_at}`;
    }
});

/**
 * Upload progress (0–100) for a not-yet-uploaded File; undefined once the
 * upload finished (the entry is removed from the map) or for saved media.
 */
const uploadPercent = computed((): number | undefined => {
    if (!props.file.sourceFile || !props.uploadProgress) {
        return undefined;
    }

    return props.uploadProgress.get(props.file.sourceFile);
});

const remove = () => {
    emit('remove', props.file);
};

const sizeClass = computed(() => {
    const sizes = {
        xs: 'forms__field_input--files__preview--xs',
        sm: 'forms__field_input--files__preview--sm',
        base: 'forms__field_input--files__preview--base',
        lg: 'forms__field_input--files__preview--lg',
        xl: 'forms__field_input--files__preview--xl',
    };

    return `${sizes[props.size] ?? sizes.base}`;
});

const open = (event: MouseEvent, file: PreviewFile) => {
    event.preventDefault();

    emit('open', file);
};

const hasActions = computed(() => {
    return props.actions && props.actions.length > 0;
});

const extension = computed(() => {
    const match = /\.([a-z0-9]{1,5})$/i.exec(props.file.name ?? '');

    return match ? match[1].toUpperCase() : '';
});
</script>
